/**
 * In-memory, per-account cache of the last data a screen displayed, so reopening a panel or tab
 * can paint that data at once while the screen's own request refreshes it (stale-while-revalidate).
 *
 * This holds presentation state only. The server stays authoritative: every reader still issues
 * its normal request and replaces the cached value with the reply. Entries live in memory for the
 * page's lifetime, are never persisted, and are dropped when the signed-in account changes. At most
 * `UI_CACHE_LIMIT` entries are kept; the least recently written entry is evicted first.
 *
 * Never cache secrets (tokens, one-time codes) here.
 *
 * @module apps/web/src/lib/uiCache
 */
import { useEffect, useState, type Dispatch, type SetStateAction } from 'react';
import { useUserStore } from '@/stores/userStore';

/** Maximum number of cached entries (200). Each entry is one screen payload, typically < 100 KB. */
export const UI_CACHE_LIMIT = 200;

/** Cached values keyed by caller-chosen keys, in least-recently-written-first order. */
const entries = new Map<string, unknown>();

/** Account whose data the cache holds; a different account empties it. */
let owner: string | null = useUserStore.getState().userId;

useUserStore.subscribe(state => {
  if (state.userId === owner) return;
  owner = state.userId;
  entries.clear();
});

/** Return the value last written under `key` for the current account, or `undefined`. */
export function readUiCache<T>(key: string): T | undefined {
  return entries.get(key) as T | undefined;
}

/**
 * Store `value` under `key`, replacing any earlier value and evicting the oldest entry beyond
 * `UI_CACHE_LIMIT`. `undefined` removes the entry.
 */
export function writeUiCache<T>(key: string, value: T | undefined): void {
  entries.delete(key);
  if (value === undefined) return;
  entries.set(key, value);
  if (entries.size > UI_CACHE_LIMIT) entries.delete(entries.keys().next().value as string);
}

/**
 * `useState` whose initial value is the cached value for `key` (falling back to `initial`) and
 * whose every committed value is written back under `key`. The key is read on mount only; a
 * component that switches keys must remount (give it a React `key`).
 */
export function useCachedState<T>(key: string, initial: T): [T, Dispatch<SetStateAction<T>>] {
  const [value, setValue] = useState<T>(() => {
    const cached = readUiCache<T>(key);
    return cached === undefined ? initial : cached;
  });
  useEffect(() => {
    writeUiCache(key, value);
  }, [key, value]);
  return [value, setValue];
}
